import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  LuMapPin, LuCalendar, LuArrowRight, LuTicket,
  LuSearch, LuX, LuChevronDown, LuBuilding2,
} from "react-icons/lu";
import storeClient from "../api/storeClient";
import { buildStorageUrl } from "../utils/storageUrl";
import formatDate from "../utils/dateFormatter";

/* ── Brand tokens ──────────────────────────────────────────── */
const T = {
  bg:        "#0c0c0c",
  surface:   "#141414",
  card:      "#181818",
  lime:      "#d4ff00",
  limeDim:   "rgba(212,255,0,0.08)",
  limeGlow:  "rgba(212,255,0,0.22)",
  white:     "#f0ede6",
  muted:     "#525252",
  border:    "rgba(255,255,255,0.07)",
  borderLime:"rgba(212,255,0,0.3)",
  red:       "#ff3b3b",
  blue:      "#4488ff",
};

/* ── Responsive hook ───────────────────────────────────────── */
function useIsMobile(breakpoint = 640) {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return mobile;
}

/* ── Font loader ───────────────────────────────────────────── */
function useFonts() {
  useEffect(() => {
    if (document.getElementById("mp-fonts")) return;
    const l = document.createElement("link");
    l.id = "mp-fonts";
    l.rel = "stylesheet";
    l.href =
      "https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Instrument+Sans:ital,wght@0,400;0,500;0,600;1,400&display=swap";
    document.head.appendChild(l);
  }, []);
}

const FONT_DISPLAY = "'Syne', sans-serif";
const FONT_BODY    = "'Instrument Sans', sans-serif";

/* ── Sales pill ────────────────────────────────────────────── */
function Pill({ event }) {
  const base = { fontFamily: FONT_BODY, fontSize: "0.65rem", letterSpacing: "0.1em",
    textTransform: "uppercase", fontWeight: 600,
    display: "inline-flex", alignItems: "center", gap: "5px",
    padding: "3px 10px", borderRadius: "999px" };

  if (event.is_sold_out)
    return <span style={{ ...base, background: "rgba(255,59,59,0.12)", color: T.red, border: "1px solid rgba(255,59,59,0.25)" }}>Agotado</span>;
  if (event.sales_status === "upcoming_sale")
    return <span style={{ ...base, background: "rgba(68,136,255,0.1)", color: T.blue, border: "1px solid rgba(68,136,255,0.25)" }}>Próximamente</span>;
  if (event.sales_status === "sale_ended")
    return <span style={{ ...base, background: "rgba(255,255,255,0.05)", color: T.muted, border: `1px solid ${T.border}` }}>Finalizado</span>;
  return (
    <span style={{ ...base, background: T.limeDim, color: T.lime, border: `1px solid ${T.borderLime}` }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.lime, animation: "pulse 2s infinite" }} />
      En venta
    </span>
  );
}

/* ── Ticker marquee ────────────────────────────────────────── */
function Ticker({ items }) {
  const text = items.map(e => `${e.name}  ·  ${e.venue_name || e.tenant_name}`).join("   —   ");
  return (
    <div style={{
      overflow: "hidden", whiteSpace: "nowrap",
      borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`,
      padding: "10px 0", background: T.surface,
    }}>
      <style>{`
        @keyframes ticker { from { transform: translateX(0) } to { transform: translateX(-50%) } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>
      <span style={{
        display: "inline-block",
        animation: "ticker 30s linear infinite",
        fontFamily: FONT_BODY,
        fontSize: "0.72rem",
        letterSpacing: "0.08em",
        color: T.muted,
        textTransform: "uppercase",
      }}>
        {text + "   —   " + text}
      </span>
    </div>
  );
}

/* ── Hero ──────────────────────────────────────────────────── */
function Hero({ total, tickerItems }) {
  const heroRef = useRef(null);
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const videoOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);
  const textY        = useTransform(scrollYProgress, [0, 1], [0, 100]);

  return (
    <section ref={heroRef} style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: T.bg, position: "relative", overflow: "hidden" }}>

      {/* Animated gradient fallback + video */}
      <motion.div style={{ opacity: videoOpacity, position: "absolute", inset: 0 }}>
        <style>{`
          @keyframes gradientShift {
            0%   { background-position: 0% 50% }
            50%  { background-position: 100% 50% }
            100% { background-position: 0% 50% }
          }
        `}</style>
        {/* Gradient fallback (always rendered behind video) */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(135deg, #0c0c0c 0%, #1a1a00 25%, #0c1a0c 50%, #001a1a 75%, #0c0c0c 100%)",
          backgroundSize: "400% 400%",
          animation: "gradientShift 12s ease infinite",
        }} />
        <video autoPlay muted loop playsInline
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.38) saturate(0.6)" }}>
          <source src={import.meta.env.VITE_HERO_VIDEO_URL || "/hero.mp4"} type="video/mp4" />
        </video>
      </motion.div>

      {/* Gradient overlays */}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(12,12,12,0.85) 0%, rgba(12,12,12,0.2) 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, #0c0c0c 0%, transparent 55%)", pointerEvents: "none" }} />

      {/* Grain */}
      <div style={{ position: "absolute", inset: 0, opacity: 0.4, pointerEvents: "none",
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.08'/%3E%3C/svg%3E")`,
        backgroundSize: "300px" }} />

      {/* Nav */}
      <motion.nav initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        style={{ position: "relative", zIndex: 20, display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "18px 20px" : "24px 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: T.lime, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 24px ${T.limeGlow}` }}>
            <LuTicket size={17} color="#0c0c0c" strokeWidth={2.5} />
          </div>
          <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: "1.25rem", color: T.white, letterSpacing: "0.02em" }}>PulseTicket</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999,
          background: "rgba(12,12,12,0.6)", backdropFilter: "blur(12px)", border: `1px solid ${T.border}` }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.lime, animation: "pulse 2s infinite" }} />
          <span style={{ fontFamily: FONT_BODY, fontSize: "0.72rem", color: T.lime, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            {total} eventos
          </span>
        </div>
      </motion.nav>

      {/* Headline */}
      <motion.div style={{ y: textY, flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end",
        position: "relative", zIndex: 10, padding: isMobile ? "0 20px 56px" : "0 40px 80px" }}>

        <motion.p initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
          style={{ fontFamily: FONT_BODY, fontSize: "0.7rem", color: T.lime, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: "1.25rem" }}>
          — Conciertos · Teatro · Deportes · Cultura
        </motion.p>

        <div style={{ overflow: "hidden" }}>
          <motion.h1 initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.28, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
            style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: isMobile ? "clamp(2rem, 11vw, 3.5rem)" : "clamp(3.5rem, 12vw, 11rem)", lineHeight: 0.9, color: T.white, letterSpacing: "-0.02em" }}>
            Tu próxima
          </motion.h1>
        </div>
        {/* "gran noche" — stacks vertically on mobile to avoid overflow */}
        <div style={{ overflow: "hidden", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "flex-end", gap: isMobile ? 0 : "0.4em" }}>
          <motion.h1 initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.4, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
            style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: isMobile ? "clamp(2rem, 11vw, 3.5rem)" : "clamp(3.5rem, 12vw, 11rem)", lineHeight: 0.9,
              color: "transparent", WebkitTextStroke: `2px ${T.lime}`, letterSpacing: "-0.02em" }}>
            gran
          </motion.h1>
          <motion.h1 initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.5, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
            style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: isMobile ? "clamp(2rem, 11vw, 3.5rem)" : "clamp(3.5rem, 12vw, 11rem)", lineHeight: 0.9, color: T.white, letterSpacing: "-0.02em" }}>
            noche
          </motion.h1>
        </div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85, duration: 0.8 }}
          style={{ fontFamily: FONT_BODY, fontStyle: "italic", fontSize: isMobile ? "0.9rem" : "1.05rem", color: "rgba(240,237,230,0.55)", fontWeight: 400,
            marginTop: "1.5rem", maxWidth: 420, lineHeight: 1.65 }}>
          Todos los promotores. Una sola plataforma.<br />Elige, compra y vívelo.
        </motion.p>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          style={{ marginTop: "3rem", display: "flex", alignItems: "center", gap: 10 }}>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2.2, repeat: Infinity }}>
            <LuChevronDown size={15} style={{ color: "rgba(240,237,230,0.28)" }} />
          </motion.div>
          <span style={{ fontFamily: FONT_BODY, fontSize: "0.65rem", color: "rgba(240,237,230,0.28)", letterSpacing: "0.22em", textTransform: "uppercase" }}>
            Explorar eventos
          </span>
        </motion.div>
      </motion.div>

      {/* Bottom ticker */}
      {tickerItems.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          style={{ position: "relative", zIndex: 20 }}>
          <Ticker items={tickerItems} />
        </motion.div>
      )}
    </section>
  );
}

/* ── Event card ────────────────────────────────────────────── */
function EventCard({ event, index, featured = false }) {
  const img = buildStorageUrl(event.image_path);
  const isMobile = useIsMobile();
  const featuredRow = featured && !isMobile;

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.055, 0.35), duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
    >
      <a href={event.can_buy ? event.event_url : undefined}
        onClick={!event.can_buy ? e => e.preventDefault() : undefined}
        style={{ display: "block", opacity: event.can_buy ? 1 : 0.5, pointerEvents: event.can_buy ? "auto" : "none" }}>
        <motion.div
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          style={{
            borderRadius: 16, overflow: "hidden",
            background: T.card,
            display: "flex", flexDirection: featuredRow ? "row" : "column",
            height: featuredRow ? 340 : "auto",
            boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
          }}
          initial={{ border: `1px solid ${T.border}` }}
          whileHover={{ y: -5, border: `1px solid ${T.borderLime}` }}
        >
          {/* Image */}
          <div style={{
            position: "relative", overflow: "hidden", flexShrink: 0,
            width: featuredRow ? "52%" : "100%",
            paddingBottom: featuredRow ? 0 : "58%",
            background: "#111",
          }}>
            {img ? (
              <motion.img src={img} alt={event.name} loading="lazy"
                whileHover={{ scale: 1.04 }} transition={{ duration: 0.5 }}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #1a1a1a, #0f0f0f)" }}>
                <LuTicket size={featured ? 48 : 32} style={{ color: "#2a2a2a" }} />
              </div>
            )}
            <div style={{ position: "absolute", inset: 0,
              background: featuredRow
                ? "linear-gradient(to right, transparent 55%, rgba(24,24,24,0.95))"
                : "linear-gradient(to top, rgba(24,24,24,0.92) 0%, rgba(24,24,24,0.1) 55%, transparent 100%)" }} />

            {/* Status top-left */}
            <div style={{ position: "absolute", top: 12, left: 12 }}>
              <Pill event={event} />
            </div>
          </div>

          {/* Content */}
          <div style={{
            padding: featuredRow ? "32px 28px" : "16px 18px 20px",
            display: "flex", flexDirection: "column",
            flex: 1, justifyContent: "space-between",
          }}>
            <div>
              {/* Promoter */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                <LuBuilding2 size={11} style={{ color: T.muted }} />
                <span style={{ fontFamily: FONT_BODY, fontSize: "0.68rem", color: T.muted, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {event.tenant_name}
                </span>
              </div>

              <h3 style={{
                fontFamily: FONT_DISPLAY, fontWeight: 700,
                fontSize: featuredRow ? "clamp(1.4rem, 2.5vw, 2rem)" : "1.05rem",
                lineHeight: 1.1, color: T.white,
                letterSpacing: featuredRow ? "-0.02em" : "-0.01em",
                marginBottom: 12,
                display: "-webkit-box", WebkitLineClamp: featuredRow ? 3 : 2,
                WebkitBoxOrient: "vertical", overflow: "hidden",
              }}>
                {event.name}
              </h3>

              {featuredRow && event.description && (
                <p style={{ fontFamily: FONT_BODY, fontStyle: "italic", fontSize: "0.85rem",
                  color: "rgba(240,237,230,0.45)", lineHeight: 1.6, marginBottom: 18,
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {event.description}
                </p>
              )}
            </div>

            <div>
              {/* Meta */}
              <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 16,
                paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <LuCalendar size={11} style={{ color: T.lime, flexShrink: 0 }} />
                  <span style={{ fontFamily: FONT_BODY, fontSize: "0.75rem", color: "rgba(240,237,230,0.6)" }}>
                    {formatDate(event.start_time, { format: "short" })} · {formatDate(event.start_time, { format: "time" })}
                  </span>
                </div>
                {event.venue_name && (
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <LuMapPin size={11} style={{ color: T.lime, flexShrink: 0 }} />
                    <span style={{ fontFamily: FONT_BODY, fontSize: "0.75rem", color: "rgba(240,237,230,0.6)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {event.venue_name}
                    </span>
                  </div>
                )}
              </div>

              {/* CTA */}
              {event.can_buy ? (
                <motion.div whileHover={{ gap: 14 }}
                  style={{ display: "flex", alignItems: "center", gap: 8,
                    fontFamily: FONT_BODY, fontWeight: 600,
                    fontSize: featuredRow ? "0.9rem" : "0.78rem",
                    color: T.lime, letterSpacing: "0.05em" }}>
                  Ver entradas
                  <motion.span whileHover={{ x: 3 }} transition={{ duration: 0.2 }}>
                    <LuArrowRight size={featured ? 16 : 13} />
                  </motion.span>
                </motion.div>
              ) : (
                <span style={{ fontFamily: FONT_BODY, fontSize: "0.75rem", color: T.muted }}>
                  {event.is_sold_out ? "Entradas agotadas" : "No disponible"}
                </span>
              )}
            </div>
          </div>
        </motion.div>
      </a>
    </motion.div>
  );
}

/* ── Search + filters ──────────────────────────────────────── */
const FILTERS = [
  { id: "all",          label: "Todos"          },
  { id: "on_sale",      label: "En venta"       },
  { id: "upcoming",     label: "Próximamente"   },
];

function Controls({ search, onSearch, filter, onFilter, count }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 36 }}>
      {/* Search */}
      <div style={{ position: "relative", flex: "1 1 260px", minWidth: 220 }}>
        <LuSearch size={14} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: T.muted, pointerEvents: "none" }} />
        <input
          type="text" value={search} onChange={e => onSearch(e.target.value)}
          placeholder="Busca eventos, promotores, recintos…"
          style={{
            width: "100%", background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: 10, padding: "10px 36px 10px 38px", outline: "none",
            fontFamily: FONT_BODY, fontSize: "0.85rem", color: T.white,
            caretColor: T.lime, boxSizing: "border-box",
          }}
        />
        <AnimatePresence>
          {search && (
            <motion.button initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.6 }}
              onClick={() => onSearch("")}
              style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex", padding: 4 }}>
              <LuX size={13} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Filter pills */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => onFilter(f.id)}
            style={{
              fontFamily: FONT_BODY, fontSize: "0.72rem", fontWeight: 600,
              letterSpacing: "0.08em", textTransform: "uppercase",
              padding: "8px 16px", borderRadius: 999, cursor: "pointer",
              background: filter === f.id ? T.lime : "transparent",
              color:      filter === f.id ? T.bg   : T.muted,
              border:     filter === f.id ? `1px solid ${T.lime}` : `1px solid ${T.border}`,
              transition: "all 0.18s",
            }}>
            {f.label}
          </button>
        ))}
      </div>

      <span style={{ fontFamily: FONT_BODY, fontSize: "0.72rem", color: T.muted, marginLeft: "auto", whiteSpace: "nowrap" }}>
        {count} {count === 1 ? "evento" : "eventos"}
      </span>
    </div>
  );
}

/* ── Skeleton ──────────────────────────────────────────────── */
function Skeleton({ featured = false }) {
  return (
    <div style={{ borderRadius: 16, overflow: "hidden", background: T.card,
      border: `1px solid ${T.border}`, height: featured ? 340 : 320,
      display: "flex", flexDirection: featured ? "row" : "column",
      animation: "skeletonPulse 1.6s ease-in-out infinite" }}>
      <style>{`@keyframes skeletonPulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
      <div style={{ background: "#1e1e1e", width: featured ? "52%" : "100%", paddingBottom: featured ? 0 : "58%", flexShrink: 0 }} />
      {featured && (
        <div style={{ flex: 1, padding: 28, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ height: 12, borderRadius: 6, background: "#1e1e1e", width: "40%" }} />
          <div style={{ height: 28, borderRadius: 8, background: "#1e1e1e", width: "80%" }} />
          <div style={{ height: 28, borderRadius: 8, background: "#1e1e1e", width: "65%" }} />
        </div>
      )}
    </div>
  );
}

/* ── Main ──────────────────────────────────────────────────── */
export default function PublicMarketplacePage() {
  useFonts();
  const isMobile = useIsMobile();

  const [events,   setEvents]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("all");

  useEffect(() => {
    storeClient.get("/public/events")
      .then(({ data }) => setEvents(data.data ?? data))
      .catch(()        => setError("No se pudieron cargar los eventos."))
      .finally(()      => setLoading(false));
  }, []);

  const filtered = events.filter(e => {
    const matchFilter =
      filter === "all" ||
      (filter === "on_sale"  && e.can_buy) ||
      (filter === "upcoming" && e.sales_status === "upcoming_sale");
    const q = search.trim().toLowerCase();
    return matchFilter && (!q ||
      e.name.toLowerCase().includes(q) ||
      e.tenant_name?.toLowerCase().includes(q) ||
      e.venue_name?.toLowerCase().includes(q));
  });

  const [featured, ...rest] = filtered;

  return (
    <div style={{ background: T.bg, minHeight: "100vh", color: T.white }}>

      {/* Hero */}
      <Hero total={events.length} tickerItems={events.slice(0, 12)} />

      {/* Events section */}
      <section style={{ padding: isMobile ? "36px 16px 80px" : "60px 40px 100px", maxWidth: 1280, margin: "0 auto" }}>

        <Controls search={search} onSearch={setSearch} filter={filter} onFilter={setFilter} count={filtered.length} />

        {loading ? (
          <div>
            <div style={{ marginBottom: 20 }}><Skeleton featured /></div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
              {[0,1,2].map(i => <Skeleton key={i} />)}
            </div>
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <p style={{ fontFamily: FONT_BODY, color: T.muted, marginBottom: 20 }}>{error}</p>
            <button onClick={() => window.location.reload()}
              style={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: "0.8rem", letterSpacing: "0.1em",
                textTransform: "uppercase", padding: "10px 24px", borderRadius: 999,
                background: T.lime, color: T.bg, border: "none", cursor: "pointer" }}>
              Reintentar
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{ textAlign: "center", padding: "80px 0" }}>
            <p style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: "2.5rem", color: T.muted, letterSpacing: "-0.02em", marginBottom: 8 }}>
              Sin resultados
            </p>
            <p style={{ fontFamily: FONT_BODY, fontStyle: "italic", color: T.muted, fontSize: "0.9rem" }}>
              Prueba con otro término o cambia el filtro.
            </p>
          </motion.div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div key={`${filter}/${search}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>

              {/* Featured card (full width) */}
              {featured && (
                <div style={{ marginBottom: 20 }}>
                  <EventCard event={featured} index={0} featured />
                </div>
              )}

              {/* Grid */}
              {rest.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
                  {rest.map((ev, i) => (
                    <EventCard key={`${ev.tenant_domain}-${ev.id}`} event={ev} index={i + 1} />
                  ))}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </section>

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${T.border}`, padding: isMobile ? "24px 16px" : "28px 40px",
        display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center",
        justifyContent: "space-between", flexWrap: "wrap", gap: isMobile ? 20 : 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: T.lime, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <LuTicket size={13} color={T.bg} strokeWidth={2.5} />
          </div>
          <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: "0.9rem", color: T.muted, letterSpacing: "0.02em" }}>
            PulseTicket
          </span>
        </div>

        <nav style={{ display: "flex", flexWrap: "wrap", gap: isMobile ? 14 : 24 }}>
          {[
            { to: "/contact", label: "Contacto" },
            { to: "/terms", label: "Términos de uso" },
            { to: "/privacy", label: "Privacidad" },
            { to: "/faq", label: "FAQ" },
          ].map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              style={{ fontFamily: FONT_BODY, fontSize: "0.75rem", color: T.muted, letterSpacing: "0.04em", textDecoration: "none" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = T.white; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = T.muted; }}
            >
              {label}
            </Link>
          ))}
        </nav>

        <p style={{ fontFamily: FONT_BODY, fontSize: "0.7rem", color: T.muted, letterSpacing: "0.06em" }}>
          © {new Date().getFullYear()} PulseTicket · Plataforma de venta de entradas
        </p>
      </footer>
    </div>
  );
}
